import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CredentialCenter from "@/components/CredentialCenter";
import ApplicationPreview from "@/components/telehealth/ApplicationPreview";
import { CrStatusBadge } from "@/components/admin/ChangeRequestsPanel";
import { TelehealthAvailability, TelehealthAppointments } from "@/components/TelehealthScheduling";
import ClientRequests from "@/components/telehealth/ClientRequests";
import TodaySchedule from "@/components/telehealth/TodaySchedule";
import CalendarSync from "@/components/telehealth/CalendarSync";
import {
  HeartPulse, LayoutDashboard, Inbox, Users, CalendarDays, FolderLock, FileText,
  Building2, Clock, LogOut, Loader2, Check, X, Upload, ShieldAlert, UserPlus, Send, Pencil, Download, Eye, ListChecks, Lock, RefreshCw,
} from "lucide-react";

const NAV = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "today", label: "Today's Schedule", icon: CalendarDays },
  { key: "requests", label: "New Client Requests", icon: Inbox },
  { key: "clients", label: "Accepted Clients", icon: Users },
  { key: "appointments", label: "Appointments", icon: CalendarDays },
  { key: "availability", label: "Availability", icon: Clock },
  { key: "calendar-sync", label: "Calendar Sync", icon: RefreshCw },
  { key: "documents", label: "Credential Center", icon: FolderLock },
  { key: "forms", label: "Forms & Consents", icon: FileText },
  { key: "profile", label: "Practice Profile", icon: Building2 },
  { key: "team", label: "Team Members", icon: UserPlus },
  { key: "activity", label: "Activity History", icon: Clock },
];

const DOC_TYPES = [
  ["business_formation", "Business formation documents"],
  ["npi_documentation", "NPI documentation"], ["authorized_representative", "Authorized representative"],
  ["other_requested", "Other requested records"],
];

const TEAM_ROLES = [
  ["organization_owner", "Organization Owner"], ["clinical_administrator", "Clinical Administrator"],
  ["scheduling_staff", "Scheduling Staff"], ["licensed_clinician", "Licensed Clinician"],
  ["records_staff", "Records Staff"], ["billing_staff", "Billing Staff"], ["read_only_auditor", "Read-Only Auditor"],
];

const inputCls = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cobalt/30";
const Empty = ({ text }) => <div className="py-14 text-center text-sm text-slate-400" data-testid="th-empty">{text}</div>;

const StatusBanner = ({ me, onSubmit, busy, onEdit, onPreview, onDownloadPdf }) => {
  const tone = { approved: "bg-emerald-50 border-emerald-200 text-emerald-700", rejected: "bg-red-50 border-red-200 text-red-600",
    suspended: "bg-red-50 border-red-200 text-red-600", info_requested: "bg-amber-50 border-amber-200 text-amber-700" }[me.status]
    || "bg-slate-50 border-slate-200 text-slate-600";
  const btn = "flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold disabled:opacity-50 transition-all";
  return (
    <div className={`rounded-2xl border px-4 py-3 flex flex-wrap items-center justify-between gap-3 ${tone}`} data-testid="th-status-banner">
      <div className="text-sm font-semibold">
        {me.status === "submitted" ? "Application Submitted — Pending Administrative Review" : `Application status: ${me.status_label}`}
      </div>
      <div className="flex flex-wrap gap-2">
        {me.status === "draft" && (
          <>
            <button onClick={onEdit} data-testid="th-continue-application" className={`${btn} bg-cobalt text-white`}>
              <Pencil className="w-3.5 h-3.5" /> Continue Application
            </button>
            <button onClick={onSubmit} disabled={busy} data-testid="th-submit-application" className={`${btn} bg-navy text-white`}>
              <Send className="w-3.5 h-3.5" /> {busy ? "Submitting…" : "Submit application for review"}
            </button>
          </>
        )}
        {me.status === "info_requested" && (
          <>
            <button onClick={onEdit} data-testid="th-edit-application" className={`${btn} bg-cobalt text-white`}>
              <Pencil className="w-3.5 h-3.5" /> Edit Application
            </button>
            <button onClick={() => document.querySelector('[data-testid="th-corrections"]')?.scrollIntoView({ behavior: "smooth" })}
              data-testid="th-view-comments" className={`${btn} bg-white border border-amber-300 text-amber-700`}>View Admin Comments</button>
            <button onClick={onSubmit} disabled={busy} data-testid="th-submit-application" className={`${btn} bg-navy text-white`}>
              <Send className="w-3.5 h-3.5" /> {busy ? "Submitting…" : "Resubmit for review"}
            </button>
          </>
        )}
        {me.status === "submitted" && (
          <>
            <button onClick={onPreview} data-testid="th-view-submitted" className={`${btn} bg-white border border-slate-300 text-slate-600`}>View submitted copy</button>
            <button onClick={onDownloadPdf} data-testid="th-download-pdf" className={`${btn} bg-navy text-white`}>
              <Download className="w-3.5 h-3.5" /> Download PDF
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default function TelehealthPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [me, setMe] = useState(undefined);
  const [dash, setDash] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loadingRows, setLoadingRows] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [demoEnabled, setDemoEnabled] = useState(false);
  useEffect(() => { api.get("/settings/demo-mode").then(({ data }) => setDemoEnabled(!!data.enabled)).catch(() => {}); }, []);

  const downloadPdf = async () => {
    try {
      const res = await api.get("/telehealth/application.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "crash_ready_provider_application.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not generate PDF"); }
  };

  const loadMe = useCallback(() => {
    api.get("/telehealth/me").then(({ data }) => setMe(data)).catch(() => setMe(null));
    api.get("/telehealth/dashboard").then(({ data }) => setDash(data)).catch(() => {});
  }, []);
  useEffect(loadMe, [loadMe]);

  const loadTab = useCallback(async (t) => {
    const map = { clients: "/telehealth/clients", forms: "/telehealth/forms",
      team: "/telehealth/team", activity: "/telehealth/activity" };
    if (!map[t]) return;
    setLoadingRows(true);
    try { const { data } = await api.get(map[t]); setRows(data); }
    catch (e) { setRows([]); if (e.response?.status === 403) toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoadingRows(false); }
  }, []);
  useEffect(() => { setRows([]); loadTab(tab); }, [tab, loadTab]);

  const submitApp = async () => {
    setBusy(true);
    try { await api.post("/telehealth/submit"); toast.success("Application submitted for admin review"); loadMe(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not submit"); }
    finally { setBusy(false); }
  };

  const act = async (rid, action, payload = {}) => {
    try {
      await api.post(`/telehealth/requests/${rid}/${action}`, payload);
      toast.success("Updated");
      loadMe();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed"); }
  };
  void act;

  if (me === undefined) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 text-cobalt animate-spin" /></div>;
  if (me === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="bg-white rounded-3xl border border-slate-100 p-8 max-w-md text-center" data-testid="th-no-application">
          <HeartPulse className="w-10 h-10 text-cobalt mx-auto mb-3" />
          <h1 className="font-heading text-xl font-bold text-navy">No provider application yet</h1>
          <p className="text-sm text-slate-500 mt-2">Start your telehealth provider application to join the Crash Ready medical network.</p>
          <button onClick={async () => { await api.post("/telehealth/apply", {}); window.location.reload(); }} data-testid="th-start-application"
            className="mt-5 px-6 py-3 rounded-full bg-cobalt text-white font-medium">Start application</button>
        </div>
      </div>
    );
  }

  const approved = me.status === "approved";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="telehealth-portal">
      <header className="bg-white border-b border-slate-100 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <HeartPulse className="w-6 h-6 text-cobalt" />
            <span className="font-heading font-extrabold text-navy">Telehealth Provider Portal</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-slate-500">{me.org?.practice_name || user?.name}</span>
            <button onClick={() => { logout(); navigate("/login"); }} data-testid="th-logout" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-navy"><LogOut className="w-4 h-4" /> Log out</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <StatusBanner me={me} onSubmit={submitApp} busy={busy} onEdit={() => setTab("profile")}
          onPreview={() => setShowPreview(true)} onDownloadPdf={downloadPdf} />
        {(me.requested_corrections || []).length > 0 && (
          <div className="mt-3 rounded-2xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-700" data-testid="th-corrections">
            <div className="font-semibold flex items-center gap-2"><ShieldAlert className="w-4 h-4" /> Requested corrections</div>
            {me.requested_corrections.map((c, i) => <div key={i} className="mt-1 text-xs">• {c.text}</div>)}
          </div>
        )}

        <div className="mt-5 flex gap-2 overflow-x-auto pb-1">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <button key={n.key} onClick={() => setTab(n.key)} data-testid={`th-nav-${n.key}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-all ${tab === n.key ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"}`}>
                <Icon className="w-3.5 h-3.5" /> {n.label}
              </button>
            );
          })}
        </div>

        <div className="mt-6">
          {tab === "dashboard" && dash && (
            <div>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="th-dashboard">
                {[["new_requests", "New requests"], ["accepted_clients", "Accepted clients"], ["appointments_today", "Appointments today"],
                  ["upcoming_appointments", "Upcoming"], ["completed_visits", "Completed visits"], ["charts_awaiting_upload", "Charts to upload"],
                  ["charts_sent", "Charts sent"], ["follow_ups_due", "Follow-ups due"], ["unread_messages", "Unread notifications"],
                  ["forms", "Active forms"], ["team_members", "Team members"]].map(([k, label]) => (
                  <div key={k} className="bg-white rounded-2xl border border-slate-100 px-4 py-3" data-testid={`th-stat-${k}`}>
                    <div className="font-heading text-2xl font-extrabold text-navy">{dash.counts?.[k] ?? 0}</div>
                    <div className="text-[11px] text-slate-500">{label}</div>
                  </div>
                ))}
              </div>
              {(dash.credential_alerts || []).length > 0 && (
                <div className="mt-4 rounded-2xl bg-red-50 border border-red-200 p-4 text-sm text-red-600" data-testid="th-credential-alerts">
                  <b>Credential expiration alerts:</b> {dash.credential_alerts.map((c) => `${c.name} (${c.expires_at})`).join(", ")}
                </div>
              )}
              {!approved && <p className="mt-4 text-xs text-slate-400">Client requests, forms and team management unlock after your application is approved by a Crash Ready administrator.</p>}
            </div>
          )}

          {tab === "today" && (!approved ? <Empty text="Today's Schedule unlocks after approval." /> : <TodaySchedule />)}
          {tab === "requests" && <ClientRequests approved={approved} demoEnabled={demoEnabled} />}
          {tab === "calendar-sync" && <CalendarSync />}

          {tab === "clients" && (
            loadingRows ? <div className="py-14 flex justify-center"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div> :
            !approved ? <Empty text="Accepted clients unlock after approval." /> :
            rows.length === 0 ? <Empty text="No accepted clients yet." /> :
            <div className="bg-white rounded-3xl border border-slate-100 divide-y divide-slate-50" data-testid="th-clients">
              {rows.map((r) => (
                <div key={r.request_id} className="p-4 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="font-semibold text-navy text-sm">{r.client_name || "Client"} <span className="font-mono text-xs text-slate-400 ml-2">{r.case_reference}</span></div>
                    <div className="text-xs text-slate-500 mt-0.5">{r.client_email} · {r.client_phone || "no phone"} · Firm: {r.law_firm || "—"}</div>
                  </div>
                  <div className="text-xs text-slate-400">Accepted {r.accepted_at ? new Date(r.accepted_at).toLocaleDateString() : ""}</div>
                </div>
              ))}
            </div>
          )}

          {tab === "appointments" && (!approved ? <Empty text="Appointments unlock after approval." /> : <TelehealthAppointments />)}
          {tab === "availability" && (!approved ? <Empty text="Availability configuration unlocks after approval." /> : <TelehealthAvailability />)}

          {tab === "documents" && (
            <div className="space-y-5" data-testid="th-credential-center">
              <CredentialCenter role="telehealth" title="Provider Credential Center" />
              <div className="bg-white rounded-3xl border border-slate-100 p-6" data-testid="th-extra-documents">
              <h3 className="font-heading font-semibold text-navy mb-1">Additional documents</h3>
              <p className="text-xs text-slate-400 mb-4">Files are stored in protected private storage and are visible only to your organization and Crash Ready administrators. Crash Ready does not represent a credential as verified until an administrator completes verification.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {DOC_TYPES.map(([k, label]) => {
                  const uploaded = me.documents?.[k];
                  return (
                    <div key={k} className="rounded-2xl border border-slate-100 p-4 flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-medium text-navy">{label}</div>
                        <div className="text-[11px] text-slate-400">{uploaded ? `Uploaded ${new Date(uploaded.uploaded_at).toLocaleDateString()} — ${uploaded.filename}` : "Not uploaded"}</div>
                      </div>
                      <label className="cursor-pointer flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold hover:bg-slate-200">
                        <Upload className="w-3.5 h-3.5" /> {uploaded ? "Replace" : "Upload"}
                        <input type="file" className="hidden" data-testid={`th-doc-${k}`} onChange={async (e) => {
                          const file = e.target.files?.[0]; if (!file) return;
                          const fd = new FormData(); fd.append("doc_type", k); fd.append("file", file);
                          try { await api.post("/telehealth/documents", fd); toast.success(`${label} uploaded`); loadMe(); }
                          catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed"); }
                        }} />
                      </label>
                    </div>
                  );
                })}
              </div>
              </div>
            </div>
          )}

          {tab === "forms" && <FormsTab approved={approved} rows={rows} reload={() => loadTab("forms")} loading={loadingRows} />}
          {tab === "profile" && <ProfileTab me={me} reload={loadMe} onSubmit={submitApp} busy={busy} onDownloadPdf={downloadPdf} />}
          {tab === "team" && <TeamTab approved={approved} rows={rows} reload={() => loadTab("team")} loading={loadingRows} />}

          {tab === "activity" && (
            loadingRows ? <div className="py-14 flex justify-center"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div> :
            rows.length === 0 ? <Empty text="No activity recorded yet." /> :
            <div className="bg-white rounded-3xl border border-slate-100 divide-y divide-slate-50" data-testid="th-activity">
              {rows.map((a) => (
                <div key={a.audit_id} className="px-4 py-2.5 text-xs flex justify-between gap-3">
                  <span className="font-medium text-navy">{a.event}</span>
                  <span className="text-slate-400">{new Date(a.at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {showPreview && (
        <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowPreview(false)}>
          <div className="bg-slate-50 rounded-3xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="th-preview-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-navy">Submitted Application</h3>
              <div className="flex gap-2">
                <button onClick={downloadPdf} data-testid="th-preview-download" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-navy text-white text-xs font-semibold"><Download className="w-3.5 h-3.5" /> PDF</button>
                <button onClick={() => setShowPreview(false)} data-testid="th-preview-close" className="p-1.5 rounded-full hover:bg-slate-200"><X className="w-4 h-4 text-slate-500" /></button>
              </div>
            </div>
            <ApplicationPreview me={me} />
          </div>
        </div>
      )}
    </div>
  );
}

const FormsTab = ({ approved, rows, reload, loading }) => {
  const [f, setF] = useState({ name: "", description: "", form_type: "new_patient", version: "1.0", required: false });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const create = async () => {
    if (!f.name) { toast.error("Form name is required"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("file", file);
      await api.post("/telehealth/forms", fd);
      toast.success("Form added"); setF({ name: "", description: "", form_type: "new_patient", version: "1.0", required: false }); setFile(null); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add form"); }
    finally { setSaving(false); }
  };
  if (!approved) return <Empty text="The forms library unlocks after your application is approved." />;
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-3xl border border-slate-100 p-6" data-testid="th-form-create">
        <h3 className="font-heading font-semibold text-navy mb-3">Add a form</h3>
        <div className="space-y-3">
          <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="th-form-name" className={inputCls} placeholder="Form name (e.g. Telehealth Consent)" />
          <input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="th-form-desc" className={inputCls} placeholder="Description" />
          <div className="grid grid-cols-2 gap-3">
            <select value={f.form_type} onChange={(e) => setF({ ...f, form_type: e.target.value })} data-testid="th-form-type" className={inputCls}>
              {["new_patient", "medical_history", "telehealth_consent", "privacy_acknowledgment", "release_authorization", "symptom_questionnaire", "concussion_screening", "follow_up_questionnaire", "practice_policies", "other"].map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
            </select>
            <input value={f.version} onChange={(e) => setF({ ...f, version: e.target.value })} data-testid="th-form-version" className={inputCls} placeholder="Version" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={f.required} onChange={(e) => setF({ ...f, required: e.target.checked })} className="accent-cobalt" /> Required form</label>
          <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="th-form-file" className="text-xs" />
          <button onClick={create} disabled={saving} data-testid="th-form-save" className="px-5 py-2.5 rounded-full bg-navy text-white text-sm font-semibold disabled:opacity-50">{saving ? "Saving…" : "Add form"}</button>
        </div>
      </div>
      <div className="bg-white rounded-3xl border border-slate-100 p-6">
        <h3 className="font-heading font-semibold text-navy mb-3">Library</h3>
        {loading ? <Loader2 className="w-5 h-5 animate-spin text-cobalt" /> : rows.length === 0 ? <Empty text="No forms yet. Signed versions are always preserved — new versions never overwrite them." /> :
          rows.map((fm) => (
            <div key={fm.form_id} className="py-2.5 border-b border-slate-50 last:border-0 flex items-center justify-between gap-2" data-testid={`th-form-${fm.form_id}`}>
              <div>
                <div className="text-sm font-medium text-navy">{fm.name} <span className="text-xs text-slate-400">v{fm.version}</span> {fm.archived && <span className="text-[10px] text-amber-600 ml-1">archived</span>}</div>
                <div className="text-[11px] text-slate-400">{fm.form_type.replace(/_/g, " ")} · {fm.required ? "required" : "optional"}</div>
              </div>
              {!fm.archived && <button onClick={async () => { await api.post(`/telehealth/forms/${fm.form_id}/archive`); reload(); }} className="text-xs text-slate-400 hover:text-red-500">Archive</button>}
            </div>
          ))}
      </div>
    </div>
  );
};

const GateNote = () => (
  <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid="th-gate-note">
    <Lock className="w-3 h-3" /> Changes require admin approval
  </span>
);

const ProfileTab = ({ me, reload, onSubmit, busy, onDownloadPdf }) => {
  const [org, setOrg] = useState(me.org || {});
  const [clinical, setClinical] = useState(me.clinical_full || {});
  const [coverage, setCoverage] = useState({ states: (me.coverage?.states || []).join(", ") });
  const [pp, setPp] = useState(me.practice_profile || {});
  const [avail, setAvail] = useState(me.availability || {});
  const [roster, setRoster] = useState((me.clinical_full?.roster) || []);
  const [nr, setNr] = useState({ name: "", credential_type: "MD", license_number: "", license_state: "", expires_at: "", npi: "" });
  const [saving, setSaving] = useState(false);
  const [missing, setMissing] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [changeRequests, setChangeRequests] = useState([]);
  const isApproved = me.status === "approved";
  const isEditable = ["draft", "info_requested"].includes(me.status);

  useEffect(() => {
    if (isApproved) api.get("/telehealth/change-requests").then(({ data }) => setChangeRequests(data)).catch(() => {});
  }, [isApproved]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/telehealth/profile", {
        org, clinical: { ...clinical, roster },
        coverage: { states: coverage.states.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean) },
        practice_profile: pp, availability: avail,
      });
      if (data.change_request) {
        toast.info(`Saved. Protected fields (${data.change_request.fields.join(", ")}) were submitted as a change request for admin approval.`, { duration: 7000 });
        api.get("/telehealth/change-requests").then(({ data: cr }) => setChangeRequests(cr)).catch(() => {});
      } else {
        toast.success(isEditable ? "Draft saved — continue anytime" : "Profile saved");
      }
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const validate = async () => {
    try {
      const { data } = await api.get("/telehealth/validate");
      setMissing(data.missing);
      if (data.complete) toast.success("Application complete — ready to submit");
      else toast.error(`Missing: ${data.missing.join(", ")}`);
    } catch { toast.error("Validation failed"); }
  };

  if (me.status === "submitted") {
    return (
      <div className="space-y-4" data-testid="th-profile-locked">
        <div className="rounded-2xl bg-slate-100 border border-slate-200 p-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-600"><Lock className="w-4 h-4" /> Editing is locked while your application is pending administrative review.</div>
          <button onClick={onDownloadPdf} data-testid="th-locked-download" className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-navy text-white text-xs font-semibold"><Download className="w-3.5 h-3.5" /> Download PDF</button>
        </div>
        <ApplicationPreview me={me} />
      </div>
    );
  }

  const O = (k, ph, label) => (
    <div><label className="text-[11px] font-semibold text-slate-500 uppercase">{label}</label>
      <input value={org[k] || ""} onChange={(e) => setOrg({ ...org, [k]: e.target.value })} data-testid={`th-org-${k}`} className={`${inputCls} mt-1`} placeholder={ph} /></div>
  );
  return (
    <div className="space-y-5" data-testid="th-profile">
      {isApproved && changeRequests.length > 0 && (
        <div className="bg-white rounded-3xl border border-slate-100 p-6" data-testid="th-change-requests">
          <h3 className="font-heading font-semibold text-navy mb-3 flex items-center gap-2"><ListChecks className="w-4 h-4 text-cobalt" /> My change requests</h3>
          {changeRequests.slice(0, 5).map((cr) => (
            <div key={cr.request_id} className="py-2 border-b border-slate-50 last:border-0 flex items-center justify-between gap-3" data-testid={`th-cr-${cr.request_id}`}>
              <div>
                <div className="text-xs font-medium text-navy">{(cr.changes || []).map((c) => c.label).join(", ")}</div>
                <div className="text-[10px] text-slate-400">{new Date(cr.created_at).toLocaleString()}{cr.info_request ? ` · Admin: ${cr.info_request.text}` : ""}{cr.decision_reason && !["pending", "info_requested"].includes(cr.status) ? ` · ${cr.decision_reason}` : ""}</div>
              </div>
              <CrStatusBadge status={cr.status} label={cr.status_label} />
            </div>
          ))}
        </div>
      )}
      {missing && missing.length > 0 && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-700" data-testid="th-missing-fields">
          <b>Complete before submitting:</b> {missing.join(", ")}
        </div>
      )}
      <div className="bg-white rounded-3xl border border-slate-100 p-6">
        <h3 className="font-heading font-semibold text-navy mb-3">Organization {isApproved && <GateNote />}</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          {O("legal_name", "Legal organization name", "Legal name")}{O("practice_name", "Public practice name", "Practice name")}{O("org_type", "e.g. Telehealth medical practice", "Organization type")}
          {O("address", "Primary office address", "Primary address")}{O("main_phone", "+1 …", "Main phone")}{O("scheduling_phone", "+1 …", "Scheduling phone")}
          {O("fax", "Secure fax", "Secure fax")}{O("general_email", "info@…", "General email")}{O("scheduling_email", "scheduling@…", "Scheduling email")}
          {O("website", "https://…", "Website")}{O("timezone", "America/Chicago", "Time zone")}{O("office_hours", "Mon–Fri 8–5", "Office hours")}
        </div>
        <div className="mt-3 grid sm:grid-cols-2 gap-3">
          <div><label className="text-[11px] font-semibold text-slate-500 uppercase">Languages (comma-sep)</label>
            <input value={(org.languages || []).join ? (org.languages || []).join(", ") : org.languages || ""} onChange={(e) => setOrg({ ...org, languages: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} data-testid="th-org-languages" className={`${inputCls} mt-1`} placeholder="English, Spanish" /></div>
          <label className="flex items-end gap-2 text-sm text-slate-600 pb-2"><input type="checkbox" checked={!!org.after_hours} onChange={(e) => setOrg({ ...org, after_hours: e.target.checked })} className="accent-cobalt" /> After-hours availability</label>
        </div>
      </div>
      <div className="bg-white rounded-3xl border border-slate-100 p-6">
        <h3 className="font-heading font-semibold text-navy mb-3">Clinical & Licenses {isApproved && <GateNote />}</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          <div><label className="text-[11px] font-semibold text-slate-500 uppercase">Provider type</label>
            <select value={clinical.provider_type || ""} onChange={(e) => setClinical({ ...clinical, provider_type: e.target.value })} data-testid="th-provider-type" className={`${inputCls} mt-1`}>
              <option value="">Select…</option>
              {["telehealth_medical_practice", "physician", "nurse_practitioner", "physician_assistant", "neurology_practice", "concussion_clinic", "other"].map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
            </select></div>
          <div><label className="text-[11px] font-semibold text-slate-500 uppercase">Liability carrier</label>
            <input value={clinical.liability_insurance?.carrier || ""} onChange={(e) => setClinical({ ...clinical, liability_insurance: { ...(clinical.liability_insurance || {}), carrier: e.target.value, active: true } })} data-testid="th-ins-carrier" className={`${inputCls} mt-1`} placeholder="Carrier" /></div>
          <div><label className="text-[11px] font-semibold text-slate-500 uppercase">Policy expires</label>
            <input type="date" value={clinical.liability_insurance?.policy_expires || ""} onChange={(e) => setClinical({ ...clinical, liability_insurance: { ...(clinical.liability_insurance || {}), policy_expires: e.target.value } })} data-testid="th-ins-expires" className={`${inputCls} mt-1`} /></div>
        </div>
        <div className="mt-4">
          <div className="text-[11px] font-semibold text-slate-500 uppercase mb-2">Licensed provider roster</div>
          {roster.map((r, i) => (
            <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-50">
              <span className="text-navy">{r.name} — {r.credential_type} · {r.license_state} · exp {r.expires_at || "—"}{r.npi ? ` · NPI ${r.npi}` : ""}</span>
              <button onClick={() => setRoster(roster.filter((_, j) => j !== i))} className="text-xs text-slate-400 hover:text-red-500">Remove</button>
            </div>
          ))}
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-6 gap-2">
            <input value={nr.name} onChange={(e) => setNr({ ...nr, name: e.target.value })} data-testid="th-roster-name" className={inputCls} placeholder="Provider name" />
            <input value={nr.credential_type} onChange={(e) => setNr({ ...nr, credential_type: e.target.value })} data-testid="th-roster-cred" className={inputCls} placeholder="MD / NP / PA" />
            <input value={nr.license_number} onChange={(e) => setNr({ ...nr, license_number: e.target.value })} data-testid="th-roster-license" className={inputCls} placeholder="License #" />
            <input value={nr.license_state} onChange={(e) => setNr({ ...nr, license_state: e.target.value.toUpperCase() })} data-testid="th-roster-state" className={inputCls} placeholder="State" />
            <input type="date" value={nr.expires_at} onChange={(e) => setNr({ ...nr, expires_at: e.target.value })} data-testid="th-roster-expires" className={inputCls} />
            <button onClick={() => { if (!nr.name || !nr.license_number) { toast.error("Name and license # required"); return; } setRoster([...roster, nr]); setNr({ name: "", credential_type: "MD", license_number: "", license_state: "", expires_at: "", npi: "" }); }}
              data-testid="th-roster-add" className="px-3 py-2 rounded-xl bg-slate-100 text-slate-600 text-xs font-semibold">Add</button>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-3xl border border-slate-100 p-6 grid sm:grid-cols-2 gap-4">
        <div>
          <h3 className="font-heading font-semibold text-navy mb-3">Coverage & Availability {isApproved && <GateNote />}</h3>
          <label className="text-[11px] font-semibold text-slate-500 uppercase">Service states (comma-sep)</label>
          <input value={coverage.states} onChange={(e) => setCoverage({ states: e.target.value })} data-testid="th-coverage-states" className={`${inputCls} mt-1`} placeholder="TX, FL" />
          <label className="flex items-center gap-2 text-sm text-slate-600 mt-3"><input type="checkbox" checked={!!avail.accepting_new_clients} onChange={(e) => setAvail({ ...avail, accepting_new_clients: e.target.checked })} data-testid="th-accepting" className="accent-cobalt" /> Accepting new clients</label>
        </div>
        <div>
          <h3 className="font-heading font-semibold text-navy mb-3">Public description</h3>
          <textarea value={pp.description || ""} onChange={(e) => setPp({ ...pp, description: e.target.value })} data-testid="th-pp-description" rows={3} className={inputCls} placeholder="Short provider description shown to clients" />
          <p className="text-[10px] text-slate-400 mt-2">License documents, insurance documents, W-9 details and internal notes are never shown publicly.</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={save} disabled={saving} data-testid="th-profile-save" className="px-6 py-3 rounded-full bg-cobalt text-white font-semibold text-sm disabled:opacity-50">
          {saving ? "Saving…" : isEditable ? "Save draft" : "Save changes"}
        </button>
        {isEditable && (
          <>
            <button onClick={validate} data-testid="th-validate" className="flex items-center gap-1.5 px-5 py-3 rounded-full bg-white border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50">
              <ListChecks className="w-4 h-4" /> Validate missing fields
            </button>
            <button onClick={() => setShowPreview(true)} data-testid="th-preview" className="flex items-center gap-1.5 px-5 py-3 rounded-full bg-white border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50">
              <Eye className="w-4 h-4" /> Preview application
            </button>
            <button onClick={onSubmit} disabled={busy} data-testid="th-profile-submit" className="flex items-center gap-1.5 px-6 py-3 rounded-full bg-navy text-white font-semibold text-sm disabled:opacity-50">
              <Send className="w-4 h-4" /> {busy ? "Submitting…" : me.status === "info_requested" ? "Resubmit for review" : "Submit for review"}
            </button>
          </>
        )}
      </div>
      {isEditable && <p className="text-xs text-slate-400">You can save a draft and continue later at any time. Nothing is sent to Crash Ready until you submit for review.</p>}

      {showPreview && (
        <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowPreview(false)}>
          <div className="bg-slate-50 rounded-3xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="th-profile-preview-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-navy">Application Preview</h3>
              <div className="flex gap-2">
                <button onClick={onDownloadPdf} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-navy text-white text-xs font-semibold"><Download className="w-3.5 h-3.5" /> PDF</button>
                <button onClick={() => setShowPreview(false)} data-testid="th-profile-preview-close" className="p-1.5 rounded-full hover:bg-slate-200"><X className="w-4 h-4 text-slate-500" /></button>
              </div>
            </div>
            <ApplicationPreview me={{ ...me, org, clinical_full: { ...clinical, roster }, coverage: { states: coverage.states.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean) }, availability: avail }} />
          </div>
        </div>
      )}
    </div>
  );
};

const TeamTab = ({ approved, rows, reload, loading }) => {
  const [m, setM] = useState({ name: "", email: "", role: "scheduling_staff" });
  if (!approved) return <Empty text="Team management unlocks after approval." />;
  return (
    <div className="bg-white rounded-3xl border border-slate-100 p-6" data-testid="th-team">
      <h3 className="font-heading font-semibold text-navy mb-3">Team members</h3>
      <div className="grid sm:grid-cols-4 gap-2 mb-4">
        <input value={m.name} onChange={(e) => setM({ ...m, name: e.target.value })} data-testid="th-team-name" className={inputCls} placeholder="Name" />
        <input value={m.email} onChange={(e) => setM({ ...m, email: e.target.value })} data-testid="th-team-email" className={inputCls} placeholder="Email" />
        <select value={m.role} onChange={(e) => setM({ ...m, role: e.target.value })} data-testid="th-team-role" className={inputCls}>
          {TEAM_ROLES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <button onClick={async () => {
          try { await api.post("/telehealth/team", m); toast.success("Team member added"); setM({ name: "", email: "", role: "scheduling_staff" }); reload(); }
          catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add"); }
        }} data-testid="th-team-add" className="px-4 py-2 rounded-full bg-navy text-white text-xs font-semibold">Add member</button>
      </div>
      {loading ? <Loader2 className="w-5 h-5 animate-spin text-cobalt" /> : rows.length === 0 ? <Empty text="No team members yet. Least-privilege roles apply to every member." /> :
        rows.map((t) => (
          <div key={t.member_id} className="py-2.5 border-b border-slate-50 last:border-0 flex items-center justify-between gap-2 text-sm">
            <div><span className="font-medium text-navy">{t.name}</span> <span className="text-slate-400 text-xs">{t.email}</span>
              <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{(TEAM_ROLES.find(([k]) => k === t.role) || [])[1] || t.role}</span>
              {!t.active && <span className="ml-1 text-[10px] text-red-500">deactivated</span>}
            </div>
            {t.active && <button onClick={async () => { await api.put(`/telehealth/team/${t.member_id}`, { active: false }); toast.success("Access removed immediately"); reload(); }}
              data-testid={`th-team-deactivate-${t.member_id}`} className="text-xs text-slate-400 hover:text-red-500">Deactivate</button>}
          </div>
        ))}
    </div>
  );
};
